import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { Company, MemberRole } from "../lib/access";

interface Perm { key: string; module_key: string; description: string | null }
interface Mod { key: string; name: string }
interface Dept { id: string; name: string }

const EDITABLE: MemberRole[] = ["admin", "staff"];

export function Permissions({ company }: { company: Company }) {
  const [perms, setPerms] = useState<Perm[]>([]);
  const [mods, setMods] = useState<Mod[]>([]);
  const [granted, setGranted] = useState<Set<string>>(new Set());
  const [depts, setDepts] = useState<Dept[]>([]);
  /** department id + permission, for the department columns. */
  const [deptGranted, setDeptGranted] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState<string | null>(null);

  const canManage = company.role === "super_admin";
  const key = (role: MemberRole, p: string) => `${role}:${p}`;
  const dkey = (dept: string, p: string) => `${dept}:${p}`;

  const load = useCallback(async () => {
    const [p, m, rp, d, dp] = await Promise.all([
      supabase.from("permissions").select("key,module_key,description"),
      supabase.from("modules").select("key,name"),
      supabase.from("role_permissions").select("role,permission_key").eq("company_id", company.id),
      supabase.from("departments").select("id,name").eq("company_id", company.id).order("name"),
      supabase.from("department_permissions").select("department_id,permission_key")
        .eq("company_id", company.id),
    ]);
    setPerms((p.data as Perm[]) ?? []);
    setMods((m.data as Mod[]) ?? []);
    setGranted(new Set(
      ((rp.data as { role: MemberRole; permission_key: string }[]) ?? [])
        .map((r) => key(r.role, r.permission_key)),
    ));
    setDepts((d.data as Dept[]) ?? []);
    setDeptGranted(new Set(
      ((dp.data as { department_id: string; permission_key: string }[]) ?? [])
        .map((r) => dkey(r.department_id, r.permission_key)),
    ));
  }, [company.id]);

  useEffect(() => { void load(); }, [load]);

  async function toggle(role: MemberRole, permission: string, on: boolean) {
    setSaving(key(role, permission));
    if (on) {
      await supabase.from("role_permissions")
        .insert({ company_id: company.id, role, permission_key: permission });
    } else {
      await supabase.from("role_permissions").delete()
        .eq("company_id", company.id).eq("role", role).eq("permission_key", permission);
    }
    setSaving(null);
    void load();
  }

  /**
   * The same permission, granted to a department instead of a role.
   *
   * These were only reachable one department at a time, inside the Departments
   * page, so nobody could see the whole picture: who can hear a recording is
   * answered by roles AND departments together, and reading half of it in each
   * of two places is how a grant gets missed.
   */
  async function toggleDept(department: string, permission: string, on: boolean) {
    setSaving(dkey(department, permission));
    if (on) {
      await supabase.from("department_permissions")
        .insert({ company_id: company.id, department_id: department, permission_key: permission });
    } else {
      await supabase.from("department_permissions").delete()
        .eq("department_id", department).eq("permission_key", permission);
    }
    setSaving(null);
    void load();
  }

  // Only the modules this company actually owns. The database would refuse a
  // grant for anything else, so there is nothing to render.
  const ownedMods = mods.filter((m) => company.modules.includes(m.key));

  return (
    <>
      <header className="page-head">
        <h1>Permissions</h1>
        <p className="muted">
          Super admins always have everything their company owns, so they are not listed.
          Only modules on your plan appear here.
        </p>
        <p className="muted small">
          A permission granted to a role applies to everyone with that role. Granted to a
          department, it applies to that department&rsquo;s members whatever their role —
          which is how a lead hears their team&rsquo;s recordings without becoming an admin.
        </p>
      </header>

      {ownedMods.length === 0 && (
        <div className="card"><p className="muted">No modules are switched on yet.</p></div>
      )}

      {ownedMods.map((mod) => (
        <section className="card permissions-wide" key={mod.key}>
          <h2>{mod.name}</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Can</th>
                  {EDITABLE.map((r) => <th key={r}>{r.replace("_", " ")}</th>)}
                  {depts.map((d) => <th key={d.id} className="dept-col">{d.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {perms.filter((p) => p.module_key === mod.key).map((p) => (
                  <tr key={p.key}>
                    <td><b>{p.description ?? p.key}</b><span className="sub">{p.key}</span></td>
                    {EDITABLE.map((r) => (
                      <td key={r}>
                        <input
                          type="checkbox"
                          disabled={!canManage || saving === key(r, p.key)}
                          checked={granted.has(key(r, p.key))}
                          onChange={(e) => void toggle(r, p.key, e.target.checked)}
                          aria-label={`${r} may ${p.description ?? p.key}`}
                        />
                      </td>
                    ))}
                    {depts.map((d) => (
                      <td key={d.id} className="dept-col">
                        <input
                          type="checkbox"
                          disabled={!canManage || saving === dkey(d.id, p.key)}
                          checked={deptGranted.has(dkey(d.id, p.key))}
                          onChange={(e) => void toggleDept(d.id, p.key, e.target.checked)}
                          aria-label={`${d.name} may ${p.description ?? p.key}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </>
  );
}
